/* eslint-disable complexity */
import React, { useState, useEffect, useRef } from "react";
import { Control, Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { FormInputProps, FormInputSelect } from "@imminently/react/ui/forms";
import {
  DatePicker,
  DatePickerProps,
  PickersActionBarProps,
  MobileDateTimePickerProps,
  MobileDateTimePicker,
  TimePicker,
} from "@mui/x-date-pickers";
import { DialogActions } from "@imminently/react/ui/core/dialog";
import Button from "@mui/material/Button";
// import { useLocaleText } from "@mui/x-date-pickers/internals/hooks/useUtils";
import { PatientTaskModes, PatientTasks } from "@/components/PatientTasks";
import { Allergy, InfectiousSymptom, Location } from "@/resources/types";
import capitalize from "lodash/capitalize";
import {
  Autocomplete,
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Text } from "@imminently/react/ui/core/text";

import { Icon } from "@/components/Icon";
import { scrollable } from "@/components/scrollable";
import { SYMPTOM_LABELS } from "@/resources/constants";
import { css, red, Theme } from "@/theme";
import { Chip } from "@/components/ui/chip";
import { useSettingsContext } from "@/pages/settings/SettingsProvider";
import { buildLocationSelectOptions } from "@/resources/locationOccupancy";
import { useLocationOccupancyState } from "@/hooks/useLocationOccupancyState";
import { get } from "lodash";
import { useAddComplaintCode } from "./addComplaintCode";
import { ComplaintCodeCard } from "@/components/ComplaintCode/ComplaintCodeList";
import { FormInputColoredSelect } from "@/components/FormInputColoredSelect";
import { getTriageLevelColours } from "@/util/triage";
import { MultiSelect, MultiSelectAutocomplete } from "@/components/MultiSelect";
import { useTypes } from "@/providers/TypesProvider";
import { useEncounter } from "@/resources/encounters";

const withAutocompleteOffSlotProps = (slotProps?: any) => ({
  ...slotProps,
  htmlInput: {
    ...slotProps?.htmlInput,
    autoComplete: "off",
  },
});

export const FormInputTime = (props: FormInputProps) => (
  <Controller
    name={props.name}
    control={props.control}
    render={({ field, fieldState: { error }, formState: { isValidating } }) => (
      <TimePicker
        {...field}
        value={field.value === null ? undefined : field.value}
        label={props.label}
        views={["hours", "minutes"]}
        format="HH:mm"
        slotProps={{
          textField: {
            helperText: error && !isValidating ? error.message : " ",
            required: props.required,
            error: !!error,
          },
        }}
      />
    )}
  />
);

export const FormInputDate = ({ control, name, label, required, ...props }: FormInputProps & DatePickerProps<any>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({
        field,
        fieldState: { error },
        formState: { isValidating }
      }) => {
        const textFieldSlotProps = props.slotProps?.textField as any;

        return (
          <DatePicker
            {...field}
            value={field.value === null ? undefined : field.value}
            label={label}
            {...props}
            slotProps={{
              ...(props.slotProps || {}),
              textField: {
                helperText: error && !isValidating ? error.message : " ",
                required: required,
                error: !!error,
                sx: { flex: 1 },
                ...textFieldSlotProps,
                onBlur: (event: any) => {
                  field.onBlur();
                  textFieldSlotProps?.onBlur?.(event);
                },
              },
            }} />
        );
      }}
    />
  );
};

const ActionBar = (props: PickersActionBarProps) => {
  const { onAccept, onClear, onCancel, onSetToday, actions, ...other } = props;
  // FIXME: we used to override this and use the localeText, but they removed this feature
  // unsure why we are even overrriding this, and the only user `FormInputDateTime` isnt even used anyway
  // const localeText = useLocaleText();
  // localeText.cancelButtonLabel
  // localeText.okButtonLabel
  return (
    <DialogActions {...other}>
      <Button variant="text" onClick={onCancel}>Cancel</Button>
      {/* <Button variant="text" onClick={onClear}>{localeText.clear}</Button> */}
      {/* <Button variant="text" onClick={onSetToday}>{localeText.today}</Button> */}
      <Button variant="text" onClick={onAccept}>Ok</Button>
    </DialogActions>
  );
};

const styles = {
  // dateTime: (theme: Theme) => css`
  //   background-color: 'red';
  //   .MuiPickersLayout-actionBar {
  //     padding: 0 1.5rem 1.5rem 1.5rem;
  //   }
  // `
  dateTime: {
    // backgroundColor: "red",
    "& .MuiDateTimePickerToolbar-timeDigitsContainer": {
      alignItems: "center",
    },
    "& .MuiPickersLayout-actionBar": {
      padding: "0 1.5rem 1.5rem 1.5rem",
    },
  },
  allergies: (theme: Theme) => css`
    color: ${theme.palette.getContrastText(theme.palette.common.white)};
    // Default background for unknown/legacy severity values (e.g., "moderate")
    background-color: ${theme.palette.grey[200]};
    &[data-severity="rash"] {
      background-color: ${theme.palette.common.yellow[100]};
    }
    &[data-severity="vomiting"] {
      background-color: ${theme.palette.common.yellow[100]};
    }
    &[data-severity="swelling"] {
      background-color: ${theme.palette.common.orange[100]};
    }
    &[data-severity="other"] {
      background-color: ${theme.palette.common.orange[100]};
    }
    &[data-severity="breathing"] {
      background-color: ${theme.palette.common.red[100]};
    }
    &[data-severity="anaphylaxis"] {
      background-color: ${theme.palette.common.red[100]};
    }
    // Legacy severity value support
    &[data-severity="moderate"] {
      background-color: ${theme.palette.common.orange[100]};
    }
  `,
};

export const FormInputDateTime = ({ control, name, label, ...props }: FormInputProps & MobileDateTimePickerProps<any>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({
        field,
        fieldState: { error },
        formState: { isValidating }
      }) => (
        <MobileDateTimePicker
          {...field}
          value={field.value === null ? undefined : field.value}
          label={label}
          {...props}
          css={styles.dateTime}
          slots={{ actionBar: ActionBar }}
          slotProps={{
            textField: {
              helperText: error && !isValidating ? error.message : " ",
              required: true,
              error: !!error,
              sx: { flex: 1 }
            },
            actionBar: {
              sx: { padding: "0 1.5rem 1.5rem 1.5rem" }
            },
            dialog: {
              // sx: { backgroundColor: "red" }
              sx: styles.dateTime,
            }
          }} />
      )}
    />
  );
};

export type FormPatientTasksProps = { name: string, control: Control<any>, mode?: PatientTaskModes };

export const FormPatientTasks = ({ name, control, mode }: FormPatientTasksProps) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <PatientTasks mode={mode} {...field} />
      )}
    />
  );
};

const AllergyItem = ({ allergy, onRemove, disabled }: { allergy: Allergy, onRemove: () => void, disabled?: boolean }) => {
  return <Chip label={allergy.description} onDelete={disabled ? undefined : onRemove} css={styles.allergies} data-severity={allergy.severity} />;
};

export const FormAllergies = ({ name, control, disabled, ...props }: FormInputProps & { disabled?: boolean }) => {
  const [severity, setSeverity] = useState<{ value: string; label: string } | null>(null);
  const [description, setDescription] = useState<string>("");
  const { allergySeverityOptions } = useTypes();

  const { append, fields, remove } = useFieldArray({ control, name });
  const allergies = fields as (Allergy & { id: string })[];

  const handleChangeDescription = (e: any) => {
    setDescription(e.target.value);
  };

  const validAllergy = description && description.trim().length > 0;

  const addAllergy = () => {
    // add allergy and force source as practitioner
    if (disabled) return;
    if (!validAllergy) return; // Prevent adding if validation fails
    append({ description, ...(severity ? { severity: severity.value } : {}), source: "practitioner" });
    // clear fields
    setDescription("");
    setSeverity(null);
  };

  return (
    <Stack direction="column" gap={2}>
      <Typography variant="h6">Allergies</Typography>
      <Stack direction="row" gap={1} alignItems="center">
        <TextField
          sx={{ flex: 2 }}
          label="Allergen"
          value={description}
          onChange={handleChangeDescription}
          disabled={!!disabled}
          required
          autoComplete="off"
          slotProps={withAutocompleteOffSlotProps()}
        />
        <Autocomplete
          sx={{
            flex: 1,
            "& .MuiInputBase-root": { height: "38.99px", padding: "0 9px" },
            "& .MuiInputBase-input": { padding: "8.5px 4px !important" },
          }}
          value={severity}
          onChange={(_, newValue) => setSeverity(newValue)}
          options={allergySeverityOptions}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          disabled={!!disabled}
          autoHighlight
          openOnFocus
          renderInput={({ inputProps, ...params }) => (
            <TextField
              {...params}
              label="Reaction"
              autoComplete="off"
              slotProps={withAutocompleteOffSlotProps({ htmlInput: inputProps })}
            />
          )}
        />
        <IconButton disabled={!!disabled || !validAllergy} onClick={addAllergy}><Icon icon="add" /></IconButton>
      </Stack>
      <Stack gap={1} flex={1} className={scrollable} sx={{ flexFlow: "row wrap", maxHeight: "12rem", overflow: "auto", scrollbarGutter: "stable" }}>
        {allergies.length === 0 && <Text>No allergies</Text>}
        {allergies.map((field, index) => (
          <AllergyItem key={field.id} allergy={field} onRemove={() => remove(index)} disabled={disabled} />
        ))}
      </Stack>

    </Stack>
  );
};

// sexOptions and genderOptions now come from TypesProvider via useTypes() hook
// These are configured in Settings > Patient Details

const INFECTIOUS_OPTIONS = Object.values(InfectiousSymptom).map((symptom) => ({
  value: symptom,
  label: SYMPTOM_LABELS[symptom],
}));

type FormSymptomsSelectProps = FormInputProps;

export const FormSymptomsSelect = (props: FormSymptomsSelectProps) => {
  const { name, control, label, ...otherProps } = props;

  return (
    <MultiSelectAutocomplete
      name={name}
      control={control}
      label={label as string || "Patient Infectious Symptoms"}
      options={INFECTIOUS_OPTIONS}
      {...otherProps}
    />
  );
};

export const FormLocationSelect = ({ name, control, label, required, ...props }: FormInputProps) => {
  const { settings } = useSettingsContext();
  const { data: occupancyState, isLoading: isOccupancyLoading, isError: isOccupancyError } = useLocationOccupancyState();
  const [showOccupiedLocations, setShowOccupiedLocations] = useState(false);
  // @ts-ignore
  const locations = (settings?.locations || []) as Location[];

  // Try to get form context to determine edit mode and current value
  // This may fail if used outside a form context, so we wrap in try/catch
  let isEditMode = false;
  let currentLocationId: string | undefined;
  try {
    const formContext = useFormContext();
    if (formContext) {
      isEditMode = formContext.watch("_isEditMode") || false;
      currentLocationId = formContext.watch(name);
    }
  } catch {
    // Not in a form context, default to create mode behavior
  }

  const applyCapacityFilter = !isOccupancyLoading && !isOccupancyError && !!occupancyState;

  const options = React.useMemo(
    () =>
      buildLocationSelectOptions(locations, {
        isEditMode,
        currentLocationId,
        showOccupiedLocations,
        occupancyCounts: occupancyState?.counts,
        applyCapacityFilter,
      }),
    [locations, isEditMode, currentLocationId, showOccupiedLocations, occupancyState, applyCapacityFilter],
  );

  // Use Autocomplete for type-ahead functionality
  return (
    <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Controller
          name={name}
          control={control}
          render={({ field, fieldState: { error } }) => {
            const selectedOption = options.find(opt => opt.value === field.value) || null;
            return (
              <Autocomplete
                value={selectedOption}
                onChange={(_, newValue) => {
                  field.onChange(newValue ? newValue.value : "");
                }}
                onBlur={field.onBlur}
                options={options}
                getOptionLabel={(option) => option.label}
                getOptionDisabled={(option) => option.disabled}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                fullWidth
                autoHighlight
                openOnFocus
                sx={{
                  "& .MuiInputBase-root": { height: "38.99px", padding: "0 9px" },
                  "& .MuiInputBase-input": { padding: "8.5px 4px !important" },
                  ...props.sx,
                }}
                renderOption={(optionProps, option) => {
                  const warnOccupied = showOccupiedLocations && option.atCapacity;
                  return (
                    <Box
                      component="li"
                      {...optionProps}
                      key={option.value}
                      sx={warnOccupied ? {
                        backgroundColor: `${red[50]} !important`,
                        "&:hover": { backgroundColor: `${red[100]} !important` },
                        "&.Mui-focused": { backgroundColor: `${red[100]} !important` },
                      } : undefined}
                    >
                      {option.label}
                    </Box>
                  );
                }}
                renderInput={({ inputProps, ...params }) => (
                  <TextField
                    {...params}
                    label={label}
                    required={required}
                    error={!!error}
                    helperText={error?.message || (isOccupancyError ? "Could not refresh location occupancy; showing all locations." : " ")}
                    variant="outlined"
                    autoComplete="off"
                    slotProps={withAutocompleteOffSlotProps({ htmlInput: inputProps })}
                  />
                )}
              />
            );
          }}
        />
      </Box>
      <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", pb: "20px" }}>
        <Tooltip title="Show all locations including fully occupied locations" arrow>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showOccupiedLocations}
                onChange={(_, checked) => setShowOccupiedLocations(checked)}
              />
            }
            label="All"
            sx={{ m: 0, flexShrink: 0 }}
            slotProps={{
              typography: {
                variant: "body2",
                sx: { whiteSpace: "nowrap" },
              },
            }}
          />
        </Tooltip>
      </Box>
    </Stack>
  );
};

export const FormTriageSelect = ({ name, control, label, required, ...props }: FormInputProps) => {
  const { settings } = useSettingsContext();

  // Get form context to access encounter ID and form values
  // FormTriageSelect is always used within a form context, so this is safe
  const formContext = useFormContext();
  const encounterId = useWatch({ control, name: "_encounterId" });
  const currentTriageValue = useWatch({ control, name });
  const currentTriageChangeReason = useWatch({ control, name: "triageChangeReason" });

  // Fetch the encounter to get the triaged timestamp and saved triage value
  // Only fetch if we have an encounter ID (edit mode)
  const { data: encounter } = useEncounter(encounterId || "", {
    enabled: !!encounterId,
  });

  // Get triaged timestamp and saved triage value from the fetched encounter
  const triaged = encounter?.triaged;
  const savedTriageValue = encounter?.atsScore ? String(encounter.atsScore) : undefined;
  const savedTriageChangeReason = (encounter as any)?.triageChangeReason;

  // Get triage update window from settings (default to 3 minutes if not set)
  const triageUpdateWindow = get(settings, "triage.updateWindow", 3) * 60 * 1000; // Convert minutes to milliseconds

  // Check if triage is outside the window
  // Only disable if triaged exists and is outside the window
  const isTriageOutsideWindow = triaged ? (Date.now() - triaged) > triageUpdateWindow : false;

  // Check if triage has changed from saved value (only if encounter was previously saved)
  // Also check if current value matches saved value (no change)
  // Use String() to ensure consistent comparison (form values might be numbers or strings)
  const currentTriageStr = currentTriageValue ? String(currentTriageValue) : "";
  const savedTriageStr = savedTriageValue ? String(savedTriageValue) : "";
  const hasTriageChanged = !!(encounterId && savedTriageStr && currentTriageStr && currentTriageStr !== savedTriageStr);
  const isTriageSameAsSaved = !!(encounterId && savedTriageStr && currentTriageStr && currentTriageStr === savedTriageStr);

// Show the reason dropdown only while the current ATS score differs from the saved ATS score.
  const shouldShowReasonDropdown = !isTriageSameAsSaved && (
    (hasTriageChanged && !isTriageOutsideWindow) ||
    (encounterId && isTriageOutsideWindow && (currentTriageChangeReason || savedTriageChangeReason))
  );

  // Get triage reasons from settings
  const triageReasons = get(settings, "triage.reasons", []) as Array<{ code: string; description: string }>;
  const firstReasonCode = triageReasons.length > 0 ? triageReasons[0].code : "";

  // Set default reason when triage changes, clear when it reverts (but keep if window expired)
  // Also ensure saved reason from encounter is set in form if it exists
  useEffect(() => {
    const currentReason = formContext.getValues("triageChangeReason");

    // Priority 1: If triage matches saved value (no change), always clear the reason
    // This handles the case where user changes triage then changes it back, or selects same value
    if (isTriageSameAsSaved && currentReason) {
      formContext.setValue("triageChangeReason", "", { shouldValidate: false });
      return;
    }

    // Priority 2: If encounter has a saved reason but form doesn't, set it (handles loading encounter with existing reason)
    // Only if triage is actually different from saved (or window expired to keep it visible)
    if (encounter && savedTriageChangeReason && !currentReason && (hasTriageChanged || isTriageOutsideWindow)) {
      formContext.setValue("triageChangeReason", savedTriageChangeReason, { shouldValidate: false });
      return;
    }

    // Priority 3: Only set default if triage changed and no reason exists (and window hasn't expired)
    if (hasTriageChanged && !isTriageOutsideWindow && firstReasonCode && !currentReason) {
      formContext.setValue("triageChangeReason", firstReasonCode, { shouldValidate: false });
      return;
    }
  }, [hasTriageChanged, isTriageSameAsSaved, firstReasonCode, encounter, savedTriageChangeReason, isTriageOutsideWindow]);

  // Animation state for the reason dropdown
  const [showAnimation, setShowAnimation] = useState(false);
  const reasonFieldRef = useRef<HTMLDivElement>(null);

  // Trigger animation when reason field appears (only when triage changes, not when just showing existing)
  useEffect(() => {
    if (hasTriageChanged) {
      setShowAnimation(true);
      const timer = setTimeout(() => {
        setShowAnimation(false);
      }, 3000); // Flash for 3 seconds
      return () => clearTimeout(timer);
    }
  }, [hasTriageChanged]);

  const triage_scores = get(settings, "encounter.triageLevels", []).map((t: any) => {
    return {
      label: t.label,
      value: t.value,
      colourPalette: t.colourPalette
    };
  });

  const triageReasonOptions = triageReasons.map(reason => ({
    value: reason.code,
    label: reason.description,
  }));

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error }, formState: { isValidating } }) => {
        // Find the selected option to get its colors
        // Normalize comparison: field.value might be a string (from ensureString), but opt.value is a number
        const fieldValueStr = field.value ? String(field.value) : "";
        const selectedOption = triage_scores.find((opt: any) => String(opt.value) === fieldValueStr) || null;
        const selectedColors = selectedOption ? getTriageLevelColours(selectedOption.colourPalette) : null;

        // Disable if triage is outside the window
        const isDisabled = isTriageOutsideWindow;

        return (
          <Stack gap={0.5}>
            {label && (
              <Text variant="caption" sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
                {label}
                {required && <Text component="span"> *</Text>}
              </Text>
            )}
            <Autocomplete
              value={selectedOption}
              onChange={(_, newValue) => {
                field.onChange(newValue ? newValue.value : "");
              }}
              onBlur={field.onBlur}
              options={triage_scores}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              disabled={isDisabled}
              fullWidth
              autoHighlight
              openOnFocus
              slotProps={{
                paper: {
                  sx: { mt: 0.5, maxHeight: "calc(4rem * 5 + 1.5rem)" },
                },
              }}
              renderOption={(optionProps, option) => {
                const colors = getTriageLevelColours(option.colourPalette);
                return (
                  <Box
                    component="li"
                    {...optionProps}
                    key={option.value}
                    sx={{
                      backgroundColor: `${colors.background} !important`,
                      border: `2px solid ${colors.border}`,
                      borderRadius: "0.5rem",
                      margin: "0.25rem 0.5rem !important",
                      fontWeight: 700,
                      fontSize: "0.875rem",
                      py: "12px !important",
                      minHeight: "auto",
                      "&:hover": {
                        backgroundColor: `${colors.background} !important`,
                        opacity: 0.9,
                      },
                      "&.Mui-focused": {
                        backgroundColor: `${colors.background} !important`,
                        opacity: 0.9,
                      },
                    }}
                  >
                    <Text bold variant="caption">
                      {option.label}
                    </Text>
                  </Box>
                );
              }}
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: selectedColors?.background || "transparent",
                  border: `2px solid ${selectedColors?.border || "rgba(0, 0, 0, 0.23)"}`,
                  borderRadius: "0.5rem",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  py: 0.5,
                  "&:hover": {
                    border: `2px solid ${selectedColors?.border || "rgba(0, 0, 0, 0.87)"}`,
                  },
                  "&.Mui-focused": {
                    border: `2px solid ${selectedColors?.border || "rgba(25, 118, 210, 1)"}`,
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  display: "none",
                },
                "& fieldset": {
                  display: "none",
                },
                "& .MuiInputBase-input": {
                  fontWeight: 700,
                  fontSize: "0.875rem",
                },
              }}
              renderInput={({ inputProps, ...params }) => (
                <TextField
                  {...params}
                  required={false}
                  error={!!error}
                  label={undefined}
                  autoComplete="off"
                  slotProps={withAutocompleteOffSlotProps({ htmlInput: inputProps })}
                />
              )}
            />
            <FormHelperText error={!!error} sx={{ mt: 0 }}>
              {error && !isValidating ? error.message : isDisabled ? "Triage cannot be changed outside the update window" : " "}
            </FormHelperText>
            {/* Triage Change Reason Dropdown - shown when triage has changed OR if reason already exists */}
            {shouldShowReasonDropdown && triageReasons.length > 0 && (
              <Box
                ref={reasonFieldRef}
                sx={{
                  mt: 1,
                  borderRadius: 1,
                  p: 0.5,
                  border: showAnimation ? "2px solid #d32f2f" : "2px solid transparent",
                  animation: showAnimation ? "flashBorder 0.5s ease-in-out 6" : "none",
                  "@keyframes flashBorder": {
                    "0%, 100%": {
                      borderColor: "#d32f2f",
                      boxShadow: "0 0 0 0 rgba(211, 47, 47, 0.4)",
                    },
                    "50%": {
                      borderColor: "#d32f2f",
                      boxShadow: "0 0 0 4px rgba(211, 47, 47, 0.2)",
                    },
                  },
                  transition: "border-color 0.3s ease",
                }}
              >
                <Controller
                  name="triageChangeReason"
                  control={control}
                  rules={{ required: "Please select a reason for changing the triage score" }}
                  render={({ field: reasonField, fieldState: { error: reasonError } }) => {
                    // Ensure value is set correctly - use saved reason if form value is empty but encounter has it
                    const fieldValue = reasonField.value || savedTriageChangeReason || "";
                    const selectedReasonOption = triageReasonOptions.find(opt => opt.value === fieldValue) || null;

                    return (
                      <Autocomplete
                        value={selectedReasonOption}
                        onChange={(_, newValue) => {
                          reasonField.onChange(newValue ? newValue.value : "");
                        }}
                        options={triageReasonOptions}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        disabled={isTriageOutsideWindow}
                        fullWidth
                        autoHighlight
                        openOnFocus
                        sx={{
                          "& .MuiInputBase-root": { height: "38.99px", padding: "0 9px" },
                          "& .MuiInputBase-input": { padding: "8.5px 4px !important" },
                        }}
                        renderInput={({ inputProps, ...params }) => (
                          <TextField
                            {...params}
                            label="Reason for triage change"
                            required
                            error={!!reasonError}
                            helperText={reasonError?.message || (isTriageOutsideWindow ? "Triage cannot be changed outside the update window" : " ")}
                            variant="outlined"
                            autoComplete="off"
                            slotProps={withAutocompleteOffSlotProps({ htmlInput: inputProps })}
                          />
                        )}
                      />
                    );
                  }}
                />
              </Box>
            )}
          </Stack>
        );
      }}
    />
  );
};

export const FormComplaintCodeSelect = ({ name, control, label, required }: FormInputProps) => {
  const openComplaintCodeModal = useAddComplaintCode();
  const { setValue } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error }, formState: { isValidating } }) => (
        <FormControl fullWidth>
          <div className="flex items-center justify-between gap-2">
            <FormLabel htmlFor={name} required={required}>{label}</FormLabel>
            <input id={name} type="hidden" autoComplete="off" {...field} />
            <Button
              variant="outlined"
              onBlur={field.onBlur}
              onClick={() => openComplaintCodeModal({
                onAdd: (code) => setValue(name, code, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                }),
              })}
            >
              {field.value ? "Change" : "Add"}
            </Button>
          </div>
          {
            field.value ? (
              <ComplaintCodeCard className="mt-2" value={field.value} selected={true} />
            ) : null
          }
          <FormHelperText error={!!error} sx={{ mt: 0 }}>
            {error && !isValidating ? error.message : " "}
          </FormHelperText>
        </FormControl>
      )} />
  );
};

/**
// TODO autocomplete is not quite what we want / is more complicated than desired
type AutoComplete = AutocompleteProps<any, undefined, undefined, undefined>;

export const FormAutocomplete = ({ control, name, label, options, ...props } : FormInputProps & Partial<AutoComplete> & Pick<AutoComplete, "options">) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({
        field,
        fieldState: { error },
        formState: { isValidating }
      }) => (
        <Autocomplete
          {...field}
          // label={label}
          options={options}
          {...props}
          getOptionLabel={(option) => option.label}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              helperText={error && !isValidating ? error.message : " "}
              required={true}
              error={!!error}
              sx={{ flex: 1 }}
            />
          )}
        />
      )}
    />
  );
};
*/

/**
// WIP an attempt to allow openOnFocus. Might end up using react-select

const asSelect = (multiple: boolean, selectProps: Partial<SelectProps> = {}) => ({
  select: true,
  SelectProps: {
    multiple,
    ...selectProps
  }
});

export const FormInputSelect = ({ name, control, label, required, multiple = false, openOnFocus = false, options, helperText, children, SelectProps: selectProps, scrollableClassName, ...props }: FormInputSelectProps & { openOnFocus?: boolean }) => {
  const selectRef = useRef(null);
  const [open, setOpen] = useState<boolean | null>(null);
  const selectOptions = options.map(({ value, label }) => (
    <MenuItem key={value} value={value}>
      {typeof children === "function" ? children({ value, label }) : label}
    </MenuItem>
  ));

  const onFocus = () => {
    if(selectRef.current && openOnFocus && open === null) {
      console.log("openOnFocus", selectRef.current);
      setOpen(true);
      selectRef.current.node.focus();
    }
    props.onFocus?.();
  };

  const _selectProps: SelectProps = {
    ...selectProps,
    inputRef: selectRef,
    // open: !!open,
    onFocus: onFocus,
    // onOpen: () => setOpen(true),
    onClose: () => {
      setOpen(false);
      if(selectRef.current) {
        // @ts-ignore
        selectRef.current.node.blur();
      }
      // event.target.blur();
    },
    // onFocus: () => setOpen(true),
    onBlur: () => {
      setOpen(null);
    }
    // onSelect: () => setOpen(true)
  };

  console.log("selectProps", _selectProps);

  return (
    <Controller
      name={name}
      control={control}
      render={({
        field,
        fieldState: { error }
      }) => (
        <TextField
          {...asSelect(multiple, _selectProps)}
          {...field}
          required={required}
          helperText={error ? error.message : helperText ?? " "}
          error={!!error}
          label={label}
          {...props}
          onChange={(event) => {
            field.onChange(event);
            setOpen(false);
          }}
        >
          {selectOptions}
        </TextField>
      )}
    />
  );
};

*/
